// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Agents in Settings → Connections. The card (design/konvo-agents/c-card.png) shows how many
// machines are connected and opens either the connect dialog or the machine list and pages
// (AgentMachineSettings.tsx).

import {openAgentsConnectModal} from '@app/features/konvo_agents/components/AgentsConnectModal';
import Agents from '@app/features/konvo_agents/state/Agents';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {KonvoConnectorCard} from '@app/features/konvo_connections/components/KonvoConnectorCard';
import {plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

/** Loads the overview while Connections is open (and on focus). */
export function useAgentsOverview(enabled: boolean): void {
	useEffect(() => {
		if (!enabled) return;
		void Agents.loadOverview();
		const onFocus = () => void Agents.loadOverview();
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [enabled]);
}

/** Whether Connections should show Agents at all (the per-person switch). */
export function agentsConnectionVisible(): boolean {
	return AgentsAccess.enabled && Agents.enabled !== false;
}

export const AgentsConnectionCard = observer(({onManage}: {onManage: () => void}) => {
	const {t} = useLingui();
	useAgentsOverview(true);
	const machines = Agents.machines;
	const connected = machines.length > 0;
	const online = Agents.onlineCount;
	const toolNames = [...new Set(machines.flatMap((machine) => machine.tools.map((tool) => tool.name)))];
	const anySend = machines.some((machine) => machine.limits.send);
	const account = connected
		? `${plural(machines.length, {one: '# machine', other: '# machines'})} · ${t`${online} online`}`
		: null;
	return (
		<KonvoConnectorCard
			logo={<IconTerminal2 size={24} stroke={1.6} />}
			name={t`Agents`}
			description={t`Your coding agents' chats in Konvo. Konvo can send them prompts when you confirm.`}
			features={[
				...(toolNames.length > 0 ? toolNames : ['T3 Code']),
				t`Read chats`,
				...(anySend || !connected ? [t`Send prompts`] : []),
			]}
			connected={connected}
			account={account}
			actionLabel={connected ? t`Manage` : t`Connect`}
			actionVariant={connected ? 'secondary' : 'primary'}
			onAction={() => (connected ? onManage() : openAgentsConnectModal())}
			secondaryActionLabel={connected ? t`Add machine` : undefined}
			onSecondaryAction={connected ? openAgentsConnectModal : undefined}
			data-flx="konvo-agents.connection-card"
		/>
	);
});
